import React, { useMemo } from 'react';
import {
  Image as RNImage,
  type ImageProps as RNImageProps,
  Platform,
  type ImageSourcePropType,
} from 'react-native';

/**
 * AppImage
 * - Uses `expo-image` when available and explicitly enabled (feature flag)
 * - Falls back to React Native `Image` otherwise
 *
 * This lets us evaluate/adopt `expo-image` without breaking builds where the dependency isn't installed yet.
 */
type AppImageProps = RNImageProps & {
  /** When true, prefers expo-image if available. Defaults to env flag. */
  preferExpo?: boolean;
  /** Optional expo-image cache policy for remote assets. */
  cachePolicy?: 'none' | 'disk' | 'memory' | 'memory-disk';
};

const getSourceUri = (source: ImageSourcePropType | undefined): string | null => {
  if (!source) {
    return null;
  }

  if (Array.isArray(source)) {
    const firstUriSource = source.find((entry) => typeof entry?.uri === 'string');
    return firstUriSource?.uri ?? null;
  }

  if (typeof source === 'object' && 'uri' in source && typeof source.uri === 'string') {
    return source.uri;
  }

  return null;
};

export function AppImage(props: AppImageProps) {
  const {
    preferExpo = process.env.EXPO_PUBLIC_USE_EXPO_IMAGE === 'true',
    cachePolicy,
    ...rest
  } = props;

  const ExpoImage = useMemo(() => {
    if (!preferExpo) return null;
    try {
      // Optional dependency; may not exist in all builds.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mod = require('expo-image');
      return mod?.Image ?? null;
    } catch {
      return null;
    }
  }, [preferExpo]);
  const sourceUri = getSourceUri(rest.source);
  const resolvedCachePolicy =
    cachePolicy ?? (sourceUri ? 'memory-disk' : undefined);

  if (ExpoImage) {
    // expo-image uses slightly different prop types; keep to common subset used in app.
    const { source, style, resizeMode, ...restProps } = rest as any;
    const contentFit =
      resizeMode === 'contain'
        ? 'contain'
        : resizeMode === 'cover'
          ? 'cover'
          : undefined;

    return (
      <ExpoImage
        source={source}
        style={style}
        contentFit={contentFit}
        cachePolicy={resolvedCachePolicy}
        // Avoid flashing during list scroll on Android in some cases.
        transition={Platform.OS === 'android' ? 0 : 120}
        {...restProps}
      />
    );
  }

  return <RNImage {...rest} />;
}


